import { adminService } from "@/api/admin/admin.service";
import type {
  TWhatsAppTemplate,
  TWhatsAppAnalyticsSummary,
  TUpdateWhatsAppTemplateRequest,
} from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Loader2, Pencil, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

const LIFECYCLE_OPTIONS = ["draft", "active", "paused", "deprecated", "archived"];

function lifecycleVariant(status?: string): "default" | "secondary" | "outline" | "destructive" {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return "default";
    case "draft":
      return "secondary";
    case "deprecated":
    case "archived":
      return "outline";
    case "paused":
      return "destructive";
    default:
      return "outline";
  }
}

function metaStatusVariant(status?: string): "default" | "secondary" | "outline" | "destructive" {
  switch ((status ?? "").toUpperCase()) {
    case "APPROVED":
      return "default";
    case "PENDING":
      return "secondary";
    case "REJECTED":
      return "destructive";
    default:
      return "outline";
  }
}

type TEditorState = {
  open: boolean;
  template: TWhatsAppTemplate | null;
  lifecycleStatus: string;
  category: string;
  isActive: boolean;
};

const EMPTY_EDITOR: TEditorState = {
  open: false,
  template: null,
  lifecycleStatus: "draft",
  category: "",
  isActive: true,
};

function AnalyticsSummaryCard({ summary }: { summary: TWhatsAppAnalyticsSummary | null }) {
  const outbound = summary?.outbound_message_types;
  const totalCommands = summary
    ? Object.values(summary.commands_by_name ?? {}).reduce((a, b) => a + b, 0)
    : 0;
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-semibold text-foreground">WhatsApp Analytics (last 7 days)</p>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Commands</p>
            <p className="text-xl font-bold text-foreground">{totalCommands}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Session msgs</p>
            <p className="text-xl font-bold text-foreground">{outbound?.session ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Template msgs</p>
            <p className="text-xl font-bold text-foreground">{outbound?.template ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Template/session</p>
            <p className="text-xl font-bold text-foreground">
              {(outbound?.template_vs_session_ratio ?? 0).toFixed(2)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function WhatsAppTemplatesPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);
  const [lifecycleFilter, setLifecycleFilter] = useState("");

  const [summary, setSummary] = useState<TWhatsAppAnalyticsSummary | null>(null);

  const [editor, setEditor] = useState<TEditorState>(EMPTY_EDITOR);
  const [isSaving, setIsSaving] = useState(false);

  const [isSyncing, setIsSyncing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  // Client mode: fetch the full list once; search/sort/paginate in-memory.
  // The lifecycle filter has no backend param, so it's applied client-side here.
  const fetcher = async (query: TDataGridQuery) => {
    const { data, error: err } = await adminService.getWhatsAppTemplates({ signal: query.signal });
    if (err) throw err;
    const items = data?.data ?? [];
    return lifecycleFilter
      ? items.filter((t) => (t.lifecycle_status ?? "") === lifecycleFilter)
      : items;
  };

  const loadSummary = useCallback(async (signal?: AbortSignal) => {
    const { data, error: err } = await adminService.getWhatsAppAnalyticsSummary(
      { duration: "7d" },
      { signal }
    );
    if (signal?.aborted) return;
    if (!err && data) setSummary(data.data ?? null);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadSummary(ctrl.signal);
    return () => ctrl.abort();
  }, [loadSummary]);

  const handleSync = async () => {
    setIsSyncing(true);
    const { data, error: err } = await adminService.syncWhatsAppTemplates();
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to sync templates",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: `Synced ${data?.data?.upserted ?? 0} template(s) from Meta` });
      reload();
    }
    setIsSyncing(false);
  };

  const handleCreateLocal = async () => {
    if (!createName.trim()) {
      toast({ variant: "destructive", title: "Template name is required" });
      return;
    }
    setIsCreating(true);
    const { error: err } = await adminService.createWhatsAppTemplateFromLocal(createName.trim());
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to create template",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Template created from local registry" });
      setCreateOpen(false);
      setCreateName("");
      reload();
    }
    setIsCreating(false);
  };

  const openEdit = (template: TWhatsAppTemplate) =>
    setEditor({
      open: true,
      template,
      lifecycleStatus: template.lifecycle_status ?? "draft",
      category: template.category ?? "",
      isActive: template.is_active ?? true,
    });

  const handleSave = async () => {
    if (!editor.template) return;
    setIsSaving(true);
    const payload: TUpdateWhatsAppTemplateRequest = {
      lifecycle_status: editor.lifecycleStatus,
      category: editor.category.trim() || null,
      is_active: editor.isActive,
    };
    const { error: err } = await adminService.updateWhatsAppTemplate(editor.template.id, payload);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to update template",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Template updated" });
      setEditor(EMPTY_EDITOR);
      reload();
    }
    setIsSaving(false);
  };

  const columns: TColumnDef<TWhatsAppTemplate>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (t) => t.name ?? "",
      cell: (t) => (
        <div>
          <div className="font-medium text-foreground">{t.name}</div>
          {t.template_key && (
            <div className="font-mono text-xs text-muted-foreground">{t.template_key}</div>
          )}
        </div>
      ),
    },
    { id: "category", header: "Category", sortable: true, accessor: (t) => t.category ?? "-" },
    { id: "language", header: "Language", sortable: true, accessor: (t) => t.language ?? "-" },
    {
      id: "status",
      header: "Meta Status",
      sortable: true,
      accessor: (t) => t.status ?? "",
      cell: (t) => <Badge variant={metaStatusVariant(t.status)}>{t.status ?? "-"}</Badge>,
    },
    {
      id: "lifecycle_status",
      header: "Lifecycle",
      sortable: true,
      accessor: (t) => t.lifecycle_status ?? "",
      cell: (t) => <Badge variant={lifecycleVariant(t.lifecycle_status)}>{t.lifecycle_status ?? "-"}</Badge>,
    },
    {
      id: "is_active",
      header: "Active",
      sortable: true,
      accessor: (t) => (t.is_active ? 1 : 0),
      cell: (t) => (
        <Badge variant={t.is_active ? "secondary" : "outline"}>{t.is_active ? "Yes" : "No"}</Badge>
      ),
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">WhatsApp Templates</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage message templates synced from Meta and the local registry
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => void handleSync()} disabled={isSyncing}>
              {isSyncing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Syncing…</>
              ) : (
                <><RefreshCw className="mr-2 h-4 w-4" />Sync from Meta</>
              )}
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Local
            </Button>
          </div>
        </div>

        <AnalyticsSummaryCard summary={summary} />

        {/* Lifecycle filter (client-side) */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Lifecycle:</span>
          <Button
            variant={lifecycleFilter === "" ? "default" : "outline"}
            size="sm"
            onClick={() => setLifecycleFilter("")}
          >
            All
          </Button>
          {LIFECYCLE_OPTIONS.map((opt) => (
            <Button
              key={opt}
              variant={lifecycleFilter === opt ? "default" : "outline"}
              size="sm"
              className="capitalize"
              onClick={() => setLifecycleFilter(opt)}
            >
              {opt}
            </Button>
          ))}
        </div>

        <DataGrid<TWhatsAppTemplate>
          getRowId={(row) => row.id}
          persistKey="admin.whatsapp-templates"
          ariaLabel="WhatsApp templates"
          data={{
            mode: "client",
            fetcher,
            deps: [reloadTick, lifecycleFilter],
            searchFields: ["name", "category", "language", "status"],
          }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name, category, language, status",
          }}
          rowActions={(t) => [
            { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => openEdit(t) },
          ]}
          emptyState={{ title: "No templates found. Sync from Meta to import them." }}
        />
      </div>

      {/* Create local dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setCreateName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Template from Local Registry</DialogTitle>
            <DialogDescription>
              Provision a template defined in the local registry against the WhatsApp provider.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Template name</label>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. welcome_message"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateLocal()} disabled={isCreating}>
              {isCreating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editor.open}
        onOpenChange={(open) => {
          if (!open) setEditor(EMPTY_EDITOR);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>{editor.template?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Lifecycle status
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={editor.lifecycleStatus}
                onChange={(e) => setEditor((s) => ({ ...s, lifecycleStatus: e.target.value }))}
              >
                {LIFECYCLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Category</label>
              <Input
                value={editor.category}
                onChange={(e) => setEditor((s) => ({ ...s, category: e.target.value }))}
                placeholder="e.g. MARKETING"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={editor.isActive}
                onChange={(e) => setEditor((s) => ({ ...s, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(EMPTY_EDITOR)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
